import React from 'react';
import * as stylex from '@stylexjs/stylex';
import type { TreeState, MenuTriggerProps, RootMenuTriggerState } from 'react-stately';
import type { AriaMenuProps, AriaButtonProps, AriaPopoverProps } from 'react-aria';
import type { Node as ReactAriaNode, ItemProps as ReactAriaItemProps, SectionProps as ReactAriaSectionProps } from '@react-types/shared';
import { Section as ReactAriaSection, Item as ReactAriaItem, useTreeState, useMenuTriggerState, useSubmenuTriggerState } from 'react-stately';
import { useMenu, useMenuSection, useMenuTrigger, useSubmenuTrigger, useMenuItem, useSeparator, mergeProps, useObjectRef } from 'react-aria';
import '../Consts.stylex';
import { Transitions } from '../Consts.stylex';
import './MenuActions.stylex';
import { CssVars } from './MenuActions.stylex';
import '../theme/Colors.stylex';
import { ColorNeutral, ColorBrand, ColorDanger } from '../theme/Colors.stylex';
import '../theme/Spaces.stylex';
import { Padding, Gap } from '../theme/Spaces.stylex';
import '../theme/Radius.stylex';
import { CornerRadius } from '../theme/Radius.stylex';
import '../theme/Fonts.stylex';
import { FontFamily, FontSize, FontWeight, LineHeight } from '../theme/Fonts.stylex';
import { Popover } from '../overlays/Popover';
import { Tooltip, TooltipSpan } from '../overlays/Tooltip';
import { Icon, type IconName } from '../images-and-icons/Icon';
import { Button, type Variant as ButtonVariant, type Tone as ButtonTone, type Size as ButtonSize } from '../actions/Button';
import { t } from 'src/primitives/I18n';

// MARK: exported types
// ====================
export type Tone = 'neutral' | 'brand' | 'danger';

// NOTE - maps to a ReScript variant type, see MenuActions.resi
type CompactTrigger = {
  icon: IconName;
  variant?: ButtonVariant;
  size?: ButtonSize;
  bold?: boolean;
};
type NormalTrigger = {
  label: string;
  icon?: IconName;
  variant?: ButtonVariant;
  tone?: ButtonTone;
  size?: ButtonSize;
};
export type Trigger = ({
  TAG: 'Compact';
} & CompactTrigger) | ({
  TAG: 'Normal';
} & NormalTrigger);

// NOTE - `children` is omitted for Item as our API uses `title` instead
export type ItemProps<T = unknown> = Omit<ReactAriaItemProps<T>, 'children'> & MenuItemCustomProps;
export type SubmenuProps<T = unknown> = ItemProps<T> & {
  children?: React.ReactNode;
};
export type SectionProps<T = unknown> = ReactAriaSectionProps<T>;

// MARK: composition API
// =====================
export type MenuItemCustomProps = {
  tone?: Tone;
  bold?: boolean;
  tooltipText?: string;
  icon?: IconName;
  endElement?: React.ReactNode;
  onAction?: () => void;
};
export const Section = ReactAriaSection;
export const Item = ReactAriaItem;
export const Submenu = ReactAriaItem;

// MARK: MenuItemAppearance
// ========================
type MenuItemAppearanceProps = {
  label: string;
  icon?: IconName;
  endElement?: React.ReactNode;
  tone?: Tone;
  disabled?: boolean;
  tooltipText?: string;
  focused?: boolean;
  keyboardFocused?: boolean;
  pressed?: boolean;
  bold?: boolean;
  submenuFocused?: boolean;
  isSubmenuItem?: boolean;
  isLinkItem?: boolean;
  isOpenLink?: boolean;
  hasStartIconSpace?: boolean;
  hasEndIconSpace?: boolean;
};
function MenuItemAppearanceComponent({
  label,
  icon,
  endElement,
  tone = 'neutral',
  tooltipText,
  disabled = false,
  focused = false,
  keyboardFocused = false,
  pressed = false,
  bold = true,
  submenuFocused = false,
  isSubmenuItem = false,
  isLinkItem = false,
  isOpenLink = false,
  hasStartIconSpace = false,
  hasEndIconSpace = false
}: MenuItemAppearanceProps) {
  const [labelIsOverflowing, setLabelIsOverflowing] = React.useState(false);
  const labelRef = React.useRef<HTMLSpanElement>(null);
  React.useLayoutEffect(() => {
    const element = labelRef.current;
    if (element) {
      setLabelIsOverflowing(element.scrollWidth > element.clientWidth);
    }
  }, [label]);
  return <Tooltip content={<TooltipSpan text={tooltipText || ''} />} disabled={!tooltipText} placement="start" arrowed delay={350} offset={-8}>
      <div title={labelIsOverflowing ? label : undefined} {...menuItemAppearanceStyleProps(disabled, focused, keyboardFocused, pressed, tone, isSubmenuItem)}>
        {hasStartIconSpace && <div style={{
        visibility: icon ? 'visible' : 'hidden'
      }}>
            <Icon name={icon || 'blank-placeholder'} bold={bold} width={16} height={16} fill={menuItemAppearanceIconColorByTone(disabled, focused, pressed)[tone]} />
          </div>}
        <span {...menuItemAppearanceLabelStyleProps(bold)} ref={labelRef}>
          {label}
        </span>
        {endElement && <div className="display-x78zum5 alignItems-x6s0dn4 marginLeft-x8x9d4c paddingLeft-xwxec1s" data-style-src="wino-gc:src/resources/navigation/MenuActions.tsx:811">
            {endElement}
          </div>}
        {hasEndIconSpace && <div {...menuItemAppearanceSubmenuIconStyleProps(isSubmenuItem, submenuFocused)}>
            <Icon name="chevron_right" bold={submenuFocused} width={16} height={16} fill={menuItemAppearanceIconColorByTone(disabled, focused, pressed)[tone]} />
          </div>}
        {isLinkItem && <div {...menuItemAppearanceLinkIconStyleProps(disabled, focused)}>
            <Icon name={isOpenLink ? 'external_link' : 'arrow_right'} width={16} height={16} fill={menuItemAppearanceIconColorByTone(disabled, focused, pressed)[tone]} />
          </div>}
      </div>
    </Tooltip>;
}
const MenuItemAppearance = React.memo(MenuItemAppearanceComponent);

// MARK: style:MenuItemAppearance
// ==============================
const menuItemAppearanceDefaultToneStyles = {
  neutral: {
    "color-kMwMTN": "color-x1w1nkkj",
    "--MenuItemAppearance_linkIcon_childBgColor-xemibqp": "--MenuItemAppearance_linkIcon_childBgColor-xemibqp-xtw1z2g",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:224"
  },
  brand: {
    "color-kMwMTN": "color-x1mzc8w9",
    "--MenuItemAppearance_linkIcon_childBgColor-xemibqp": "--MenuItemAppearance_linkIcon_childBgColor-xemibqp-xtw1z2g",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:228"
  },
  danger: {
    "color-kMwMTN": "color-x75p7gn",
    "--MenuItemAppearance_linkIcon_childBgColor-xemibqp": "--MenuItemAppearance_linkIcon_childBgColor-xemibqp-xtw1z2g",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:232"
  }
};
const menuItemAppearanceHoveredToneStyles = {
  neutral: {
    "backgroundColor-kWkggS": "backgroundColor-x9m8vy9",
    "--MenuItemAppearance_linkIcon_childBgColor-xemibqp": "--MenuItemAppearance_linkIcon_childBgColor-xemibqp-xn2p1sd",
    "color-kMwMTN": "color-xsqyr71",
    "outlineColor-kjBf7l": "outlineColor-x1rcsaz8",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:238"
  },
  brand: {
    "backgroundColor-kWkggS": "backgroundColor-x1s5ep7y",
    "--MenuItemAppearance_linkIcon_childBgColor-xemibqp": "--MenuItemAppearance_linkIcon_childBgColor-xemibqp-xp40lae",
    "color-kMwMTN": "color-x16f91ol",
    "outlineColor-kjBf7l": "outlineColor-x1pqgiyg",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:244"
  },
  danger: {
    "backgroundColor-kWkggS": "backgroundColor-x16nig27",
    "--MenuItemAppearance_linkIcon_childBgColor-xemibqp": "--MenuItemAppearance_linkIcon_childBgColor-xemibqp-x1lfi54p",
    "color-kMwMTN": "color-x1vryi6o",
    "outlineColor-kjBf7l": "outlineColor-xgbbo6b",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:250"
  }
};
const menuItemAppearancePressedToneStyles = {
  neutral: {
    "backgroundColor-kWkggS": "backgroundColor-xct4vau",
    "--MenuItemAppearance_linkIcon_childBgColor-xemibqp": "--MenuItemAppearance_linkIcon_childBgColor-xemibqp-xohnha9",
    "color-kMwMTN": "color-xb7zkul",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:258"
  },
  brand: {
    "backgroundColor-kWkggS": "backgroundColor-x1ybj0jp",
    "--MenuItemAppearance_linkIcon_childBgColor-xemibqp": "--MenuItemAppearance_linkIcon_childBgColor-xemibqp-xpplvtb",
    "color-kMwMTN": "color-x1pzenjb",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:264"
  },
  danger: {
    "backgroundColor-kWkggS": "backgroundColor-xakmtdx",
    "--MenuItemAppearance_linkIcon_childBgColor-xemibqp": "--MenuItemAppearance_linkIcon_childBgColor-xemibqp-x1ja9ccd",
    "color-kMwMTN": "color-x1d1w0zy",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:269"
  }
};
const menuItemAppearanceDisabledToneStyles = {
  neutral: {
    "color-kMwMTN": "color-xyemlsm",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:276"
  },
  brand: {
    "color-kMwMTN": "color-xyemlsm",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:277"
  },
  danger: {
    "color-kMwMTN": "color-xyemlsm",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:278"
  }
};
const menuItemAppearanceIconColorByTone = (disabled: boolean, focused: boolean, pressed: boolean) => {
  if (disabled) {
    return {
      neutral: ColorNeutral.iconDisabled,
      brand: ColorNeutral.iconDisabled,
      danger: ColorNeutral.iconDisabled
    };
  } else if (pressed) {
    return {
      neutral: ColorNeutral.iconPressed,
      brand: ColorBrand.iconAccent,
      danger: ColorDanger.iconAccent
    };
  } else if (focused) {
    return {
      neutral: ColorNeutral.iconAccentHover,
      brand: ColorBrand.iconHover,
      danger: ColorDanger.iconHover
    };
  } else {
    return {
      neutral: ColorNeutral.iconAccent,
      brand: ColorBrand.iconDefault,
      danger: ColorDanger.iconDefault
    };
  }
};
const menuItemAppearanceStylesByTone = (disabled: boolean, focused: boolean, pressed: boolean) => {
  if (disabled) {
    return menuItemAppearanceDisabledToneStyles;
  } else if (pressed) {
    return menuItemAppearancePressedToneStyles;
  } else if (focused) {
    return menuItemAppearanceHoveredToneStyles;
  } else {
    return menuItemAppearanceDefaultToneStyles;
  }
};
const menuItemAppearanceSubmenuIconStyleProps = (isSubmenuItem: boolean, submenuFocused: boolean) => ({
  0: {
    className: "visibility-xlshs6z display-x78zum5 flex-x98rzlu justifyContent-x13a6bvl marginRight-x1qscbpv transformOrigin-xee5nap transform-x4zgh4k transition-xsyhkzf",
    "data-style-src": "wino-gc:src/resources/navigation/MenuActions.tsx:817"
  },
  2: {
    className: "display-x78zum5 flex-x98rzlu justifyContent-x13a6bvl marginRight-x1qscbpv transformOrigin-xee5nap transform-x4zgh4k transition-xsyhkzf visibility-xnpuxes",
    "data-style-src": "wino-gc:src/resources/navigation/MenuActions.tsx:817; wino-gc:src/resources/navigation/MenuActions.tsx:827"
  },
  1: {
    className: "visibility-xlshs6z display-x78zum5 flex-x98rzlu justifyContent-x13a6bvl marginRight-x1qscbpv transformOrigin-xee5nap transition-xsyhkzf transform-x3oybdh",
    "data-style-src": "wino-gc:src/resources/navigation/MenuActions.tsx:817; wino-gc:src/resources/navigation/MenuActions.tsx:830"
  },
  3: {
    className: "display-x78zum5 flex-x98rzlu justifyContent-x13a6bvl marginRight-x1qscbpv transformOrigin-xee5nap transition-xsyhkzf visibility-xnpuxes transform-x3oybdh",
    "data-style-src": "wino-gc:src/resources/navigation/MenuActions.tsx:817; wino-gc:src/resources/navigation/MenuActions.tsx:827; wino-gc:src/resources/navigation/MenuActions.tsx:830"
  }
})[!!isSubmenuItem << 1 | !!submenuFocused << 0];
const menuItemAppearanceLinkIconStyleProps = (disabled: boolean, focused: boolean) => ({
  0: {
    className: "position-x10l6tqk right-x19up5dg overflow-xb3r6kr alignSelf-xamitd3 width-x1izajxt opacity-x1ea75x3 transition-x1hgg8ub transitionDuration-xx6bhzk boxShadow-xwwcz7 backgroundColor-xi4815x",
    "data-style-src": "wino-gc:src/resources/navigation/MenuActions.tsx:833"
  },
  2: {
    className: "position-x10l6tqk right-x19up5dg overflow-xb3r6kr alignSelf-xamitd3 transition-x1hgg8ub transitionDuration-xx6bhzk boxShadow-xwwcz7 backgroundColor-xi4815x width-x1kky2od opacity-x1hc1fzr",
    "data-style-src": "wino-gc:src/resources/navigation/MenuActions.tsx:833; wino-gc:src/resources/navigation/MenuActions.tsx:846"
  },
  1: {
    className: "position-x10l6tqk right-x19up5dg overflow-xb3r6kr alignSelf-xamitd3 width-x1izajxt transition-x1hgg8ub transitionDuration-xx6bhzk boxShadow-xwwcz7 backgroundColor-xi4815x opacity-xg01cxk",
    "data-style-src": "wino-gc:src/resources/navigation/MenuActions.tsx:833; wino-gc:src/resources/navigation/MenuActions.tsx:845"
  },
  3: {
    className: "position-x10l6tqk right-x19up5dg overflow-xb3r6kr alignSelf-xamitd3 transition-x1hgg8ub transitionDuration-xx6bhzk boxShadow-xwwcz7 backgroundColor-xi4815x width-x1kky2od opacity-xg01cxk",
    "data-style-src": "wino-gc:src/resources/navigation/MenuActions.tsx:833; wino-gc:src/resources/navigation/MenuActions.tsx:846; wino-gc:src/resources/navigation/MenuActions.tsx:845"
  }
})[!!focused << 1 | !!disabled << 0];
const menuItemAppearanceStyleProps = (disabled: boolean, focused: boolean, keyboardFocused: boolean, pressed: boolean, tone: Tone, isSubmenuItem: boolean) => stylex.props(styles.MenuItemAppearance_root, (disabled || isSubmenuItem) && styles.MenuItemAppearance_disabled, keyboardFocused && styles.MenuItemAppearance_keyboardFocused, menuItemAppearanceStylesByTone(disabled, focused, pressed)[tone]);
const menuItemAppearanceLabelStyleProps = (bold: boolean) => ({
  0: {
    className: "fontFamily-x1q3ln6d fontSize-x1egw0ox fontWeight-x1fkahbb lineHeight-x7mozim overflow-xb3r6kr textOverflow-xlyipyv whiteSpace-xuxw1ft",
    "data-style-src": "wino-gc:src/resources/navigation/MenuActions.tsx:802"
  },
  1: {
    className: "fontFamily-x1q3ln6d fontSize-x1egw0ox lineHeight-x7mozim overflow-xb3r6kr textOverflow-xlyipyv whiteSpace-xuxw1ft fontWeight-x168tpfk",
    "data-style-src": "wino-gc:src/resources/navigation/MenuActions.tsx:802; wino-gc:src/resources/navigation/MenuActions.tsx:794"
  }
})[!!bold << 0];

// MARK: MenuItem
// ==============
type MenuItemNode<T> = ReactAriaNode<T> & MenuItemCustomProps;
type MenuItemProps<T> = {
  item: MenuItemNode<T>;
  state: TreeState<T>;
  submenuOpened?: boolean;
  submenuFocused?: boolean;
  hasStartIconSpace?: boolean;
  hasEndIconSpace?: boolean;
};
function MenuItemComponent<T extends object>(props: MenuItemProps<T>, ref: React.ForwardedRef<HTMLDivElement>) {
  const {
    item,
    state,
    submenuOpened,
    ...menuItemAriaProps
  } = props;
  const itemRef = useObjectRef(ref);
  const {
    menuItemProps,
    isPressed,
    isFocused,
    isFocusVisible,
    isDisabled
  } = useMenuItem({
    ...menuItemAriaProps,
    key: item.key || item.props.title,
    onAction: item.props.onAction
  }, state, itemRef as React.RefObject<HTMLDivElement>);
  const ElementType: React.ElementType = item.props.href ? 'a' : 'div';
  const {
    title,
    icon,
    endElement,
    tone,
    tooltipText,
    bold
  } = item.props;
  return <ElementType ref={itemRef as any} {...menuItemProps}>
      <MenuItemAppearance label={title} icon={icon} endElement={endElement} tone={tone} bold={bold} tooltipText={tooltipText} disabled={isDisabled} pressed={isPressed && !submenuOpened} focused={isFocused || submenuOpened} keyboardFocused={isFocusVisible} submenuFocused={props.submenuFocused} isSubmenuItem={submenuOpened !== undefined} isLinkItem={item.props.href !== undefined} isOpenLink={item.props.target === '_blank'} hasStartIconSpace={props.hasStartIconSpace} hasEndIconSpace={props.hasEndIconSpace} />
    </ElementType>;
}
const ForwardedMenuItemComponent = React.forwardRef(MenuItemComponent);
const MenuItem = React.memo(ForwardedMenuItemComponent) as typeof ForwardedMenuItemComponent;

// MARK: SubmenuItem
// =================
type SubmenuItemProps<T> = AriaMenuProps<T> & {
  item: MenuItemNode<T>;
  state: TreeState<T>;
  rootTriggerState: RootMenuTriggerState;
  parentMenuRef: React.RefObject<HTMLDivElement>;
  hasStartIconSpace?: boolean;
  hasEndIconSpace?: boolean;
};
function SubmenuItemComponent<T extends object>(props: SubmenuItemProps<T>) {
  const {
    item,
    state,
    rootTriggerState,
    parentMenuRef,
    ...rootAriaProps
  } = props;
  const [submenuFocused, setSubmenuFocused] = React.useState(false);
  const triggerRef = React.useRef(null);
  const submenuRef = React.useRef(null);
  const submenuTriggerState = useSubmenuTriggerState({
    triggerKey: item.key ?? item.props.title
  }, rootTriggerState);
  const {
    submenuTriggerProps: menuItemAriaProps,
    submenuProps: submenuAriaProps,
    popoverProps: popoverAriaProps
  } = useSubmenuTrigger({
    parentMenuRef: parentMenuRef,
    submenuRef: submenuRef,
    delay: 200
  }, submenuTriggerState, triggerRef);
  React.useEffect(() => {
    if (!submenuTriggerState.isOpen) {
      setSubmenuFocused(false);
    }
  }, [submenuTriggerState.isOpen]);

  // NOTE - this implementation is likely to be ducktaping
  // as there is no react aria documentation on submenu yet.
  const closeAllOnKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      submenuTriggerState.closeAll();
    }
  };
  const overridedMenuItemAriaProps = mergeProps(menuItemAriaProps, {
    onKeyDown: closeAllOnKeyDown
  });
  const overridedSubmenuAriaProps = mergeProps(submenuAriaProps, {
    onKeyDown: closeAllOnKeyDown
  });
  return <>
      <MenuItem {...overridedMenuItemAriaProps} key={item.key} item={item} state={state} submenuOpened={submenuTriggerState.isOpen} submenuFocused={submenuFocused} hasStartIconSpace={props.hasStartIconSpace} hasEndIconSpace={props.hasEndIconSpace} ref={triggerRef} />
      {submenuTriggerState.isOpen && <Popover ariaProps={popoverAriaProps as AriaPopoverProps} placement="end top" offset={5} crossOffset={-9} state={submenuTriggerState} triggerRef={triggerRef}>
          <Menu {...rootAriaProps} {...overridedSubmenuAriaProps as any} rootTriggerState={rootTriggerState} ref={submenuRef} onFocusChange={setSubmenuFocused}>
            {item.props.children}
          </Menu>
        </Popover>}
    </>;
}
const SubmenuItem = React.memo(SubmenuItemComponent);

// MARK: MenuSection
// =================
type MenuSectionProps<T> = {
  item: ReactAriaNode<T>;
  state: TreeState<T>;
  rootTriggerState: RootMenuTriggerState;
  parentMenuRef: React.RefObject<HTMLDivElement>;
  hasStartIconSpace?: boolean;
  hasEndIconSpace?: boolean;
};
function MenuSectionComponent<T extends object>(props: MenuSectionProps<T>) {
  const {
    item,
    state,
    rootTriggerState,
    parentMenuRef,
    ...rootAriaProps
  } = props;
  const {
    itemProps,
    groupProps
  } = useMenuSection({
    heading: item.rendered,
    'aria-label': item['aria-label']
  });
  const {
    separatorProps
  } = useSeparator({
    elementType: 'li'
  });
  return <>
      {item.key !== state.collection.getFirstKey() && <li {...separatorProps} className="width-x1ll2v24 marginBlock-x10bxtdj marginInlineStart-x1hs919v borderWidth-x4hv7ue borderStyle-x1y0btm7 borderColor-xmweg0l" data-style-src="wino-gc:src/resources/navigation/MenuActions.tsx:850" />}
      <li {...itemProps}>
        <ul {...groupProps}>
          {[...item.childNodes].map(item => item.hasChildNodes ? <SubmenuItem {...rootAriaProps as any} key={item.key} item={item as MenuItemNode<T>} state={state} rootTriggerState={rootTriggerState} parentMenuRef={parentMenuRef} hasStartIconSpace={props.hasStartIconSpace} hasEndIconSpace={props.hasEndIconSpace} /> : <MenuItem key={item.key} item={item as MenuItemNode<T>} state={state} hasStartIconSpace={props.hasStartIconSpace} hasEndIconSpace={props.hasEndIconSpace} />)}
        </ul>
      </li>
    </>;
}
const MenuSection = React.memo(MenuSectionComponent) as typeof MenuSectionComponent;

// MARK: Menu
// ==========
type MenuProps<T> = AriaMenuProps<T> & MenuTriggerProps & {
  rootTriggerState: RootMenuTriggerState;
  onFocusChange?: (focused: boolean) => void;
};
function MenuComponent<T extends object>(props: MenuProps<T>, ref: React.ForwardedRef<HTMLDivElement> | null) {
  const {
    rootTriggerState,
    onFocusChange,
    ...rootAriaProps
  } = props;
  const state = useTreeState({
    ...rootAriaProps,
    children: props.children
  });
  const menuRef = useObjectRef(ref);
  const {
    menuProps
  } = useMenu(rootAriaProps, state, menuRef as React.RefObject<HTMLDivElement>);
  React.useEffect(() => {
    onFocusChange?.(state.selectionManager.isFocused);
  }, [state.selectionManager.isFocused, onFocusChange]);
  const {
    hasStartIconSpace,
    hasEndIconSpace
  } = React.useMemo(() => {
    const items = [...state.collection].flatMap(item => item.type === 'section' ? [...item.childNodes] : [item]);
    return {
      hasStartIconSpace: items.some(item => item.props.icon),
      hasEndIconSpace: items.some(item => item.hasChildNodes)
    };
  }, [state.collection]);
  return <div {...menuProps} className="minWidth-xktpd3l maxWidth-xaka53j padding-x170jadj" data-style-src="wino-gc:src/resources/navigation/MenuActions.tsx:770" ref={menuRef as React.RefObject<HTMLDivElement>}>
      {[...state.collection].map(item => item.type === 'section' ? <MenuSection {...rootAriaProps as any} key={item.key} item={item} state={state} rootTriggerState={rootTriggerState} parentMenuRef={menuRef} hasStartIconSpace={hasStartIconSpace} hasEndIconSpace={hasEndIconSpace} /> : item.hasChildNodes ? <SubmenuItem {...rootAriaProps as any} key={item.key} item={item as MenuItemNode<T>} state={state} rootTriggerState={rootTriggerState} parentMenuRef={menuRef} hasStartIconSpace={hasStartIconSpace} hasEndIconSpace={hasEndIconSpace} /> : <MenuItem key={item.key} item={item as MenuItemNode<T>} state={state} hasStartIconSpace={hasStartIconSpace} hasEndIconSpace={hasEndIconSpace} />)}
    </div>;
}
const ForwardedMenuComponent = React.forwardRef(MenuComponent);
const Menu = React.memo(ForwardedMenuComponent) as typeof ForwardedMenuComponent;

// MARK: MenuButton
// ================
type MenuButtonProps = AriaButtonProps & MenuTriggerProps & {
  trigger: Trigger;
  opened: boolean;
  disabled?: boolean;
};
function MenuButtonComponent(props: MenuButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) {
  const {
    trigger,
    opened,
    disabled,
    ...ariaProps
  } = props;

  // CompactTrigger
  if (trigger.TAG === 'Compact') {
    const {
      variant,
      icon,
      bold,
      size
    } = trigger;
    return <Button ariaProps={ariaProps} ariaLabel={t('More actions...')} icon={icon} bold={bold} variant={variant || 'outline'} tone={{
      NAME: 'neutral',
      VAL: {
        quiet: true
      }
    }} size={size || 'medium'} active={opened} disabled={disabled} ref={ref} />;
  }
  // NormalTrigger
  const {
    variant,
    tone,
    size,
    label,
    icon
  } = trigger;
  return <Button ariaProps={ariaProps} label={label} icon={icon} trailingIcon={opened ? 'chevron_up' : 'chevron_down'} variant={variant || 'outline'} tone={tone || {
    NAME: 'neutral',
    VAL: {}
  }} size={size || 'medium'} active={opened} disabled={disabled} ref={ref} />;
}
const ForwardedMenuButtonComponent = React.forwardRef(MenuButtonComponent);
const MenuButton = React.memo(ForwardedMenuButtonComponent) as typeof ForwardedMenuButtonComponent;

// MARK: root
// ==========
type MenuActionsProps<T> = AriaMenuProps<T> & MenuButtonProps & {
  trigger: Trigger;
};
function MenuActionsComponent<T extends Record<string, unknown>>(props: MenuActionsProps<T>) {
  const {
    trigger,
    disabled,
    ...rootAriaProps
  } = props;
  const triggerRef = React.useRef(null);
  const triggerState = useMenuTriggerState(rootAriaProps);
  const {
    menuTriggerProps: menuTriggerAriaProps,
    menuProps: menuAriaProps
  } = useMenuTrigger<T>({
    isDisabled: disabled
  }, triggerState, triggerRef);

  // NOTE - ducktape: detect clicks outside the menu system to close all
  // `shouldCloseOnInteractOutside` doesn't work properly with nested submenus
  React.useEffect(() => {
    if (!triggerState.isOpen) return;
    const handleMouseDown = (event: MouseEvent) => {
      const isInsideMenuGroup = (node: Node) => node && (node as Element).closest('[role="menu"]');
      if (!isInsideMenuGroup(event.target as Node)) {
        triggerState.close();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [triggerState.isOpen]);
  return <>
      <MenuButton {...menuTriggerAriaProps} trigger={trigger} opened={triggerState.isOpen} disabled={disabled} ref={triggerRef} />
      {triggerState.isOpen && <Popover placement="bottom end" state={triggerState} triggerRef={triggerRef}>
          <Menu {...rootAriaProps as any} {...menuAriaProps} rootTriggerState={triggerState} />
        </Popover>}
    </>;
}
const MemoizedMenuActionsComponent = React.memo(MenuActionsComponent) as typeof MenuActionsComponent;
export type { MenuActionsProps };
export { MemoizedMenuActionsComponent as MenuActions };

// MARK: style
const styles = {
  MenuItemAppearance_root: {
    "position-kVAEAm": "position-x1n2onr6",
    "overflow-kVQacm": "overflow-xb3r6kr",
    "display-k1xSpc": "display-x78zum5",
    "gap-kOIVth": "gap-xqcaodp",
    "padding-kmVPX3": "padding-xb4tss4",
    "borderRadius-kaIpWk": "borderRadius-x1qd5jr3",
    "cursor-kkrTdU": "cursor-x1ypdohk",
    "outline-kI3sdo": "outline-x1a2a7pz",
    "transition-kmkexE": "transition-x4enhow",
    "--MenuItemAppearance_linkIcon_childOpacity-x1tyax4n": "--MenuItemAppearance_linkIcon_childOpacity-x1tyax4n-xw91otp --MenuItemAppearance_linkIcon_childOpacity-x1tyax4n-x8f8350",
    "--MenuItemAppearance_linkIcon_childWidth-xeq703w": "--MenuItemAppearance_linkIcon_childWidth-xeq703w-x17uqsf9 --MenuItemAppearance_linkIcon_childWidth-xeq703w-x1mvatzl",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:775"
  },
  MenuItemAppearance_keyboardFocused: {
    "outlineStyle-k3XXqK": "outlineStyle-xaatb59",
    "outlineWidth-kMeerF": "outlineWidth-x1de99jn",
    "outlineOffset-kInvED": "outlineOffset-x1y3gkto",
    "transitionProperty-k1ekBW": "transitionProperty-x13b0p5u",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:795"
  },
  MenuItemAppearance_disabled: {
    "cursor-kkrTdU": "cursor-x1h6gzvc",
    $$css: "wino-gc:src/resources/navigation/MenuActions.tsx:801"
  }
};